// Pure calculation library for the restart-fee / cancellation calculator.
// Both the public version (embedded on the Terms page) and the internal
// admin tool import this. Every rule mirrors a clause from the Terms of
// Service so the UI can deep-link back to the corresponding section.
//
// All amounts are in EUR, excluding 21% VAT. Inputs are validated lightly
// (clamped to non-negative numbers) so the UI can pass raw form values.

export const PROJECT_STAGE = Object.freeze({
  discovery: 'discovery',     // Briefing / scoping — deposit may or may not be paid.
  preMid:    'preMid',        // Deposit paid (30%), pre-midpoint review.
  postMid:   'postMid',       // Deposit + midpoint paid (70%), pre-delivery.
});

export const EVENT_TYPE = Object.freeze({
  delay:       'delay',       // Client-side delay >= 2 weeks → 10% restart fee.
  cancel:      'cancel',      // Project terminated by the client.
  scopeChange: 'scopeChange', // Major out-of-brief change — quoted separately.
});

const STAGE_LABEL = {
  discovery: 'Discovery / pre-deposit',
  preMid:    'Pre-midpoint',
  postMid:   'Post-midpoint',
};

const EVENT_LABEL = {
  delay:       'Client-side delay (≥ 2 weeks)',
  cancel:      'Cancellation',
  scopeChange: 'Scope change / change-order',
};

const HOURLY_RATE = 75;        // Out-of-support-window hourly rate (€/h).
const EXTRA_REVISION = 120;    // Per extra revision round (€).

const clampNum = (value, min = 0) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return min;
  return Math.max(min, n);
};

const eur = (n) => Math.round(n);

// Returns:
//   {
//     amountDue:   number  // additional € to invoice the client now
//     refundable:  number  // € to refund the client (already paid > retained)
//     retained:    number  // € retained out of payments already made
//     paidToDate:  number  // helper — what they've already paid based on stage
//     lineItems:   [{ label, amount, kind, policyRef }]
//     summary:     string  // one-line plain-text recap
//   }
export function calcRestartFee(input = {}) {
  const contractTotal = clampNum(input.contractTotal);
  const stage = PROJECT_STAGE[input.stage] || input.stage || PROJECT_STAGE.preMid;
  const event = EVENT_TYPE[input.event] || input.event || EVENT_TYPE.delay;
  const delayWeeks = clampNum(input.delayWeeks);
  const productionDonePct = Math.min(100, clampNum(input.productionDonePct));
  const extraRevisions = clampNum(input.extraRevisions);
  const outOfWindowHours = clampNum(input.outOfWindowHours);

  // Payment schedule: 30% deposit / 40% midpoint / 30% delivery.
  const deposit = contractTotal * 0.3;
  const midpoint = contractTotal * 0.4;

  // Payments made depend on the stage the project is in.
  const paidToDate =
    stage === PROJECT_STAGE.discovery
      ? 0
      : stage === PROJECT_STAGE.preMid
        ? deposit
        : deposit + midpoint;

  const lineItems = [];

  // ---- 1. Event-driven core fee --------------------------------------
  if (event === EVENT_TYPE.delay) {
    if (delayWeeks >= 2 && contractTotal > 0) {
      lineItems.push({
        label: `Restart fee — ${delayWeeks} week${delayWeeks === 1 ? '' : 's'} client-side delay (10% of contract)`,
        amount: eur(contractTotal * 0.1),
        kind: 'fee',
        policyRef: 'timelines',
      });
    } else {
      lineItems.push({
        label: 'Delay below 2-week threshold — no restart fee',
        amount: 0,
        kind: 'info',
        policyRef: 'timelines',
      });
    }
  }

  if (event === EVENT_TYPE.cancel) {
    if (stage === PROJECT_STAGE.discovery) {
      lineItems.push({
        label: 'Cancelled before kick-off — discovery time billed at hourly rate',
        amount: 0,
        kind: 'info',
        policyRef: 'cancellation',
      });
    } else if (stage === PROJECT_STAGE.preMid) {
      lineItems.push({
        label: 'Deposit retained (30%, non-refundable)',
        amount: eur(deposit),
        kind: 'retained',
        policyRef: 'cancellation',
      });
    } else if (stage === PROJECT_STAGE.postMid) {
      lineItems.push({
        label: 'Deposit retained (30%, non-refundable)',
        amount: eur(deposit),
        kind: 'retained',
        policyRef: 'cancellation',
      });
      if (productionDonePct < 50) {
        const refundedHalf = midpoint * 0.5;
        lineItems.push({
          label: `Midpoint partially retained (50% of 40% — production at ${productionDonePct}%)`,
          amount: eur(midpoint - refundedHalf),
          kind: 'retained',
          policyRef: 'cancellation',
        });
        lineItems.push({
          label: 'Midpoint refundable to client (50% of 40%)',
          amount: -eur(refundedHalf),
          kind: 'refund',
          policyRef: 'cancellation',
        });
      } else {
        lineItems.push({
          label: `Midpoint fully retained (production at ${productionDonePct}% — past 50% threshold)`,
          amount: eur(midpoint),
          kind: 'retained',
          policyRef: 'cancellation',
        });
        lineItems.push({
          label: 'Deliverables handed over in current state against full payment of all invoiced amounts to date',
          amount: 0,
          kind: 'info',
          policyRef: 'cancellation',
        });
      }
    }
  }

  if (event === EVENT_TYPE.scopeChange) {
    lineItems.push({
      label: 'Scope change quoted separately as a change-order — not part of the restart-fee schedule',
      amount: 0,
      kind: 'info',
      policyRef: 'revisions',
    });
  }

  // ---- 2. Add-on fees regardless of event ---------------------------
  if (extraRevisions > 0) {
    lineItems.push({
      label: `Extra revision rounds × ${extraRevisions} (@ €${EXTRA_REVISION}/round)`,
      amount: eur(extraRevisions * EXTRA_REVISION),
      kind: 'fee',
      policyRef: 'revisions',
    });
  }
  if (outOfWindowHours > 0) {
    lineItems.push({
      label: `Out-of-support-window time × ${outOfWindowHours}h (@ €${HOURLY_RATE}/h)`,
      amount: eur(outOfWindowHours * HOURLY_RATE),
      kind: 'fee',
      policyRef: 'support-window',
    });
  }

  // ---- 3. Aggregate ------------------------------------------------
  let retained = 0;
  let extraFees = 0;
  let refunds = 0;
  for (const item of lineItems) {
    if (item.kind === 'retained') retained += item.amount;
    else if (item.kind === 'fee') extraFees += item.amount;
    else if (item.kind === 'refund') refunds += -item.amount; // amounts stored negative for display
  }

  // amountDue = additional invoicing now (extra fees on top of what was paid).
  // refundable = € to send back to the client.
  const amountDue = eur(extraFees);
  const refundable = eur(Math.max(0, refunds));

  const summaryParts = [];
  if (amountDue > 0) summaryParts.push(`Invoice client: €${amountDue.toLocaleString('nl-BE').replace(/,/g, '.')}`);
  if (refundable > 0) summaryParts.push(`Refund to client: €${refundable.toLocaleString('nl-BE').replace(/,/g, '.')}`);
  if (retained > 0) summaryParts.push(`Retained: €${retained.toLocaleString('nl-BE').replace(/,/g, '.')}`);
  if (summaryParts.length === 0) summaryParts.push('No fees apply — project continues without restart cost.');

  return {
    amountDue,
    refundable,
    retained: eur(retained),
    paidToDate: eur(paidToDate),
    lineItems,
    summary: summaryParts.join(' · '),
    inputs: {
      contractTotal: eur(contractTotal),
      stage,
      stageLabel: STAGE_LABEL[stage] || stage,
      event,
      eventLabel: EVENT_LABEL[event] || event,
      delayWeeks,
      productionDonePct,
      extraRevisions,
      outOfWindowHours,
    },
  };
}

// Human-readable Markdown breakdown — used by the admin tool's
// copy-to-clipboard button so Sander can paste straight into an invoice or
// email thread.
export function formatRestartMarkdown(result) {
  if (!result) return '';
  const lines = [];
  lines.push(`### Restart / cancellation breakdown`);
  lines.push('');
  lines.push(`- **Contract total:** €${result.inputs.contractTotal.toLocaleString('nl-BE').replace(/,/g, '.')} (excl. 21% VAT)`);
  lines.push(`- **Stage:** ${result.inputs.stageLabel}`);
  lines.push(`- **Event:** ${result.inputs.eventLabel}`);
  if (result.inputs.event === 'delay') lines.push(`- **Delay:** ${result.inputs.delayWeeks} week(s)`);
  if (result.inputs.event === 'cancel' && result.inputs.stage === 'postMid') {
    lines.push(`- **Production complete:** ${result.inputs.productionDonePct}%`);
  }
  if (result.inputs.extraRevisions > 0) lines.push(`- **Extra revisions:** ${result.inputs.extraRevisions}`);
  if (result.inputs.outOfWindowHours > 0) lines.push(`- **Out-of-window hours:** ${result.inputs.outOfWindowHours}h`);
  lines.push('');
  lines.push(`#### Line items`);
  result.lineItems.forEach((li) => {
    const sign = li.amount < 0 ? '−' : '';
    const amt = Math.abs(li.amount).toLocaleString('nl-BE').replace(/,/g, '.');
    lines.push(`- ${li.label} — ${sign}€${amt}`);
  });
  lines.push('');
  lines.push(`**${result.summary}**`);
  return lines.join('\n');
}
